import React from 'react'
import { formatCurrency, formatDate } from '../../utils/formatters'

/**
 * PrintableInvoice Component
 *
 * Clean, professional invoice layout optimized for printing
 * Follows print best practices:
 * - A4 paper size (210mm x 297mm)
 * - Proper margins
 * - Black & white optimized
 * - No unnecessary graphics
 * - Clear hierarchy
 */
const PrintableInvoice = ({ invoice }) => {
  if (!invoice) {
    return <div className="p-8 text-center">لا توجد بيانات للفاتورة</div>
  }

  return (
    <div className="printable-invoice">
      {/* Header Section */}
      <div className="invoice-header">
        <div className="company-info">
          <h1>شركة الشامل للتوصيل</h1>
          <p>نظام إدارة التوصيل والفواتير</p>
        </div>
        <div className="invoice-meta">
          <h2>فاتورة</h2>
          <p><strong>رقم الفاتورة:</strong> {invoice.invoiceNumber}</p>
          <p><strong>التاريخ:</strong> {formatDate(invoice.createdAt)}</p>
          <p><strong>الحالة:</strong> {getStatusText(invoice.status)}</p>
        </div>
      </div>

      {/* Divider */}
      <div className="divider"></div>

      {/* Customer Information */}
      <div className="customer-section">
        <h3>بيانات العميل</h3>
        <div className="customer-details">
          <p><strong>الاسم:</strong> {invoice.customer?.name}</p>
          <p><strong>الهاتف:</strong> {invoice.customer?.phone}</p>
          <p><strong>العنوان:</strong> {invoice.customer?.address || 'غير محدد'}</p>
        </div>
      </div>

      {/* Line Items Table */}
      <div className="items-section">
        <h3>تفاصيل الفاتورة</h3>
        <table className="items-table">
          <thead>
            <tr>
              <th>#</th>
              <th>المنتج</th>
              <th>الكمية</th>
              <th>السعر</th>
              <th>الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lineItems?.map((item, index) => {
              // Calculate price from total if unit_price is 0 (legacy invoices fix)
              const displayPrice = item.price > 0 ? item.price : (item.total / item.quantity)

              return (
              <tr key={item.id || index}>
                <td>{index + 1}</td>
                <td>
                  <div className="item-name">{item.productName || item.product?.name}</div>
                  {item.product?.description && (
                    <div className="item-description">{item.product.description}</div>
                  )}
                </td>
                <td>{item.quantity}</td>
                <td>{formatCurrency(displayPrice)}</td>
                <td>{formatCurrency(item.total)}</td>
              </tr>
            )})}
          </tbody>
        </table>
      </div>

      {/* Totals Section */}
      <div className="totals-section">
        <div className="totals-row">
          <span>المجموع الفرعي:</span>
          <span>{formatCurrency(invoice.subtotal)}</span>
        </div>
        <div className="totals-row">
          <span>الضريبة ({(invoice.tax / invoice.subtotal * 100).toFixed(0)}%):</span>
          <span>{formatCurrency(invoice.tax)}</span>
        </div>
        <div className="totals-row total">
          <span>الإجمالي النهائي:</span>
          <span>{formatCurrency(invoice.total)}</span>
        </div>
      </div>

      {/* Notes Section */}
      {invoice.notes && (
        <div className="notes-section">
          <h3>ملاحظات</h3>
          <p>{invoice.notes}</p>
        </div>
      )}

      {/* Footer */}
      <div className="invoice-footer">
        <p>شكراً لتعاملكم معنا</p>
        <p className="footer-note">
          تم إنشاء هذه الفاتورة بواسطة نظام الشامل للتوصيل
        </p>
      </div>
    </div>
  )
}

// Helper function for status text
const getStatusText = (status) => {
  const statusMap = {
    'draft': 'مسودة',
    'pending': 'قيد الانتظار',
    'paid': 'مدفوعة',
    'cancelled': 'ملغية'
  }
  return statusMap[status] || status
}

export default PrintableInvoice
